import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, applyNodeChanges, applyEdgeChanges, useReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowLeft, Sparkles, Play, ToggleLeft, ToggleRight, Loader2, Rocket, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import SubAccountLayout, { useSubAccount } from '../../components/SubAccountLayout';
import CallFlowNode from '../../components/callflow/CallFlowNode';
import FlowEdge from '../../components/callflow/FlowEdge';
import FlowSettingsRail from '../../components/callflow/FlowSettingsRail';
import NodeConfigDrawer from '../../components/callflow/NodeConfigDrawer';
import { decorateEdges, getUnwiredHandleIds } from '../../components/callflow/edgeDecorator';
import { tidyLayout, legacyToNodes } from '../../components/callflow/layoutUtils';
import { migrateFlow, makeQueue, wire } from '../../components/callflow/flowMigrate';
import { NODE_DEFAULTS } from '../../components/callflow/nodeTypes';
import '../../components/callflow/flowStyles';

const nodeTypes = ['start', 'menu', 'time_router', 'tag_filter', 'contact_filter', 'ai_agent', 'transfer', 'hangup', 'queue', 'department']
  .reduce((acc, t) => ({ ...acc, [t]: CallFlowNode }), {});
const edgeTypes = { vox: FlowEdge };
const MINIMAP = { start: '#34d399', menu: '#38bdf8', tag_filter: '#38bdf8', contact_filter: '#38bdf8', hangup: '#fb7185', queue: '#fbbf24', department: '#2dd4bf' };

const cleanNodes = (ns) => ns.map(({ id, type, position, data }) => ({ id, type, position, data }));
const cleanEdges = (es) => es.map(({ id, source, target, sourceHandle, targetHandle }) => ({ id, source, target, sourceHandle, targetHandle, type: 'smoothstep', animated: true }));
const graphSig = (ns, es) => JSON.stringify([cleanNodes(ns), cleanEdges(es)]);

/* Draft autosave: every canvas change is saved as a draft ~1s later. */
function useDraftAutosave({ api, subAccountId, flowId, nodes, edges, ready, rf }) {
  const savedSig = useRef(null);
  const [saveState, setSaveState] = useState('saved');
  const [hasDraft, setHasDraft] = useState(false);
  const timer = useRef(null);
  const sig = useMemo(() => graphSig(nodes, edges), [nodes, edges]);
  useEffect(() => {
    if (!ready || savedSig.current === null || sig === savedSig.current) return undefined;
    setSaveState('dirty');
    const t = setTimeout(async () => {
      if (sig === savedSig.current) return;
      setSaveState('saving');
      try {
        await api.put(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}`, { nodes: cleanNodes(nodes), edges: cleanEdges(edges), canvas_viewport: rf.getViewport(), draft: true });
        savedSig.current = sig; setHasDraft(true); setSaveState('saved');
      } catch (err) { setSaveState('dirty'); toast.error(err?.response?.data?.detail || 'Auto-save failed'); }
    }, 1000);
    timer.current = t;
    return () => clearTimeout(t);
  }, [sig, ready]); // eslint-disable-line react-hooks/exhaustive-deps
  const latest = useRef(null);
  latest.current = { dirty: ready && savedSig.current !== null && sig !== savedSig.current, nodes, edges };
  useEffect(() => {
    const flush = () => {
      const l = latest.current;
      if (!l?.dirty) return;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sub-accounts/${subAccountId}/call-routing/${flowId}`, {
        method: 'PUT', keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ nodes: cleanNodes(l.nodes), edges: cleanEdges(l.edges), draft: true }),
      }).catch(() => {});
    };
    window.addEventListener('beforeunload', flush);
    return () => { window.removeEventListener('beforeunload', flush); flush(); };
  }, [subAccountId, flowId]);
  const markSaved = useCallback((s, draft) => { clearTimeout(timer.current); savedSig.current = s; setHasDraft(draft); setSaveState('saved'); }, []);
  return { saveState, hasDraft, markSaved, sig };
}

function PublishControls({ saveState, hasDraft, publishing, onPublish }) {
  const pending = hasDraft || saveState !== 'saved';
  return (
    <>
      <span className="text-[11px] flex items-center gap-1.5 whitespace-nowrap" style={{ color: 'var(--vox-text-secondary)' }} data-testid="canvas-autosave-status">
        {saveState === 'saving' ? <><Loader2 size={11} className="animate-spin" />Saving…</> : saveState === 'dirty' ? 'Unsaved changes' : <><CheckCircle2 size={11} className="text-emerald-400" />Saved</>}
      </span>
      {hasDraft && <span className="px-2 py-0.5 rounded-full whitespace-nowrap text-[10px] font-semibold bg-amber-400/15 text-amber-600 dark:text-amber-300 border border-amber-400/30" data-testid="canvas-unpublished-badge">Unpublished changes</span>}
      <button onClick={onPublish} disabled={!pending || publishing}
        className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[11px] font-bold transition-colors ${pending ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-[#08090d] hover:shadow-lg hover:shadow-amber-400/30' : 'bg-black/5 dark:bg-white/5 text-slate-400 dark:text-gray-500 cursor-not-allowed'}`}
        data-testid="canvas-publish">
        {publishing ? <Loader2 size={13} className="animate-spin" /> : pending ? <Rocket size={13} /> : <CheckCircle2 size={13} />}
        {pending ? 'Publish' : 'Published'}
      </button>
    </>
  );
}

function RunResult({ result, onClose }) {
  return (
    <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
      className="absolute bottom-4 left-1/2 -translate-x-1/2 w-[420px] max-w-[90%] rounded-xl p-4 shadow-2xl border border-teal-400/30" style={{ background: 'var(--vox-chrome-bg)' }} data-testid="run-result">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-teal-500 dark:text-teal-300 font-bold">Simulation result</span>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-900 dark:hover:text-white text-xs" data-testid="run-result-close">×</button>
      </div>
      {result.error ? <p className="text-rose-400 text-sm">{result.error}</p> : (
        <div className="text-xs" style={{ color: 'var(--vox-text-secondary)' }}>Path: <span className="font-mono text-teal-500 dark:text-teal-300">{(result.path || []).join(' → ')}</span></div>
      )}
    </motion.div>
  );
}

function CanvasInner() {
  const { subAccountId, flowId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  useSubAccount();
  const { isDarkMode } = useTheme();
  const rf = useReactFlow();
  const flowRef = useRef(null);

  const [flow, setFlow] = useState(null);
  const [agents, setAgents] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [lineLimit, setLineLimit] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [ready, setReady] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [pulsed, setPulsed] = useState({});
  const [publishing, setPublishing] = useState(false);
  const [versionKey, setVersionKey] = useState(0);
  const nodesRef = useRef(nodes); nodesRef.current = nodes;
  const edgesRef = useRef(edges); edgesRef.current = edges;
  const reconnectOk = useRef(true);

  const { saveState, hasDraft, markSaved, sig } = useDraftAutosave({ api, subAccountId, flowId, nodes, edges, ready, rf });

  useEffect(() => {
    (async () => {
      try {
        const [fRes, aRes, pRes] = await Promise.all([
          api.get(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}`),
          api.get(`/api/sub-accounts/${subAccountId}/agents`),
          api.get(`/api/sub-accounts/${subAccountId}/phone-numbers`),
        ]);
        const f = fRes.data;
        const phones = Array.isArray(pRes.data) ? pRes.data : (pRes.data?.phone_numbers || []);
        setFlow(f); setAgents(aRes.data?.agents || aRes.data || []); setPhoneNumbers(phones);
        const useDraft = f.has_draft && f.draft_nodes?.length;
        const srvNodes = (useDraft ? f.draft_nodes : f.nodes) || [];
        const srvEdges = (useDraft ? f.draft_edges : f.edges) || [];
        const seed = srvNodes.length ? { nodes: srvNodes, edges: srvEdges } : legacyToNodes(f);
        const m = migrateFlow(seed.nodes, seed.edges, phones);
        const ns = m.nodes.map((n) => (n.type === 'start' && !('ai_cap' in (n.data || {})) ? { ...n, data: { ...n.data, ai_cap: f.settings?.ai_cap || null } } : n));
        markSaved(m.changed ? graphSig(srvNodes, srvEdges) : graphSig(ns, m.edges), !!f.has_draft);
        setNodes(ns); setEdges(m.edges); setReady(true);
        if (m.changed) toast.message('Updated this flow to the new layout: every department now has a hold queue in front of it. Publish when you are happy.');
        api.get(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}/queue`).then((r) => setLineLimit(r.data?.line_limit)).catch(() => {});
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to load flow');
      }
    })();
  }, [subAccountId, flowId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── Wiring: one wire per output; Departments are always fed by a Hold Queue ─── */
  const connect = useCallback((conn, replaceId) => {
    const ns = nodesRef.current;
    let es = edgesRef.current.filter((e) => e.id !== replaceId);
    const src = ns.find((n) => n.id === conn.source);
    const tgt = ns.find((n) => n.id === conn.target);
    if (!src || !tgt || src.id === tgt.id || tgt.type === 'start') return;
    let target = tgt.id;
    const extraEdges = [];
    const extraNodes = [];
    if (tgt.type === 'department' && src.type !== 'queue') {
      const qEdge = es.find((e) => e.target === tgt.id && ns.find((n) => n.id === e.source)?.type === 'queue');
      if (qEdge) target = qEdge.source;
      else {
        const q = makeQueue({ x: tgt.position.x, y: tgt.position.y - 190 });
        extraNodes.push(q); extraEdges.push(wire(q.id, 'out', tgt.id)); target = q.id;
        toast.message('Added a Hold Queue in front of this department.');
      }
    }
    const handle = conn.sourceHandle || 'out';
    es = es.filter((e) => !(e.source === src.id && (e.sourceHandle || 'out') === handle));
    if (extraNodes.length) setNodes([...ns, ...extraNodes]);
    setEdges([...es, wire(src.id, handle, target), ...extraEdges]);
  }, []);

  const onNodesChange = useCallback((changes) => setNodes((ns) => applyNodeChanges(changes, ns)), []);
  const onEdgesChange = useCallback((changes) => setEdges((es) => applyEdgeChanges(changes, es)), []);
  const onConnect = useCallback((conn) => connect(conn), [connect]);
  const onConnectEnd = useCallback((event, state) => {
    if (state?.isValid || !state?.fromNode || state.fromHandle?.type === 'target') return;
    const el = event?.target?.closest?.('.react-flow__node');
    const targetId = el?.getAttribute('data-id');
    if (targetId) connect({ source: state.fromNode.id, sourceHandle: state.fromHandle?.id, target: targetId });
  }, [connect]);
  const onReconnectStart = useCallback(() => { reconnectOk.current = false; }, []);
  const onReconnect = useCallback((oldEdge, conn) => { reconnectOk.current = true; connect(conn, oldEdge.id); }, [connect]);
  const onReconnectEnd = useCallback((_, edge) => {
    if (!reconnectOk.current) setEdges((es) => es.filter((e) => e.id !== edge.id));
    reconnectOk.current = true;
  }, []);

  const onDragOver = useCallback((e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }, []);
  const onDrop = useCallback((e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('application/voxinity-flow-node');
    if (!type) return;
    if (type === 'start' && nodesRef.current.some((n) => n.type === 'start')) { toast.error('A flow can only have one Start step.'); return; }
    const position = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const id = `n_${type}_${Date.now().toString(36)}`;
    const node = { id, type, position, data: { ...(NODE_DEFAULTS[type] || {}) } };
    if (type === 'department') {
      const q = makeQueue(position);
      node.position = { x: position.x, y: position.y + 190 };
      setNodes((ns) => [...ns, q, node]);
      setEdges((es) => [...es, wire(q.id, 'out', id)]);
      toast.message('Department added with its Hold Queue.');
    } else setNodes((ns) => [...ns, node]);
    setSelectedId(id);
  }, [rf]);

  const patchNode = useCallback((id, patch, replace = false) => {
    setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, data: replace ? { label: n.data?.label, ...patch } : { ...n.data, ...patch } } : n)));
  }, []);
  const deleteNodeById = useCallback((id) => {
    setNodes((ns) => ns.filter((n) => n.id !== id));
    setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
    setSelectedId((s) => (s === id ? null : s));
  }, []);

  const updateFlowPhoneNumber = useCallback(async (num) => {
    try {
      const r = await api.put(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}`, { phone_number: num });
      setFlow(r.data);
      toast.success(r.data?._rebind?.conflict_with?.agent_name ? `Detached from "${r.data._rebind.conflict_with.agent_name}" and bound ${num}` : `Bound ${num} to this flow`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not update number'); }
  }, [api, subAccountId, flowId]);

  const tidy = useCallback(() => {
    setNodes((ns) => tidyLayout(ns, edgesRef.current));
    setTimeout(() => rf.fitView({ padding: 0.2, duration: 400 }), 50);
  }, [rf]);

  const publish = useCallback(async () => {
    setPublishing(true);
    try {
      const r = await api.post(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}/publish`, { nodes: cleanNodes(nodes), edges: cleanEdges(edges) });
      markSaved(sig, false); setVersionKey((k) => k + 1);
      toast.success(`Published version ${r.data.version}. Live calls now use it.`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Publish failed'); } finally { setPublishing(false); }
  }, [nodes, edges, sig, subAccountId, flowId, api, markSaved]);

  const toggleEnabled = useCallback(async () => {
    try {
      const r = await api.put(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}`, { enabled: !flow.enabled });
      setFlow(r.data); toast.success(r.data.enabled ? 'Flow enabled' : 'Flow disabled');
    } catch { toast.error('Toggle failed'); }
  }, [flow, subAccountId, flowId, api]);

  const runSimulation = useCallback(async () => {
    setRunning(true); setRunResult(null); setPulsed({});
    try {
      await api.put(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}`, { nodes: cleanNodes(nodes), edges: cleanEdges(edges), draft: true });
      markSaved(sig, true);
      const r = await api.post(`/api/sub-accounts/${subAccountId}/call-routing/${flowId}/simulate`, {});
      setRunResult(r.data);
      (r.data?.steps || []).filter((s) => s.node_id).forEach((s, i) => {
        setTimeout(() => setPulsed((p) => ({ ...p, [s.node_id]: s.status })), i * 700);
        setTimeout(() => setPulsed((p) => { const n = { ...p }; delete n[s.node_id]; return n; }), i * 700 + 2400);
      });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Simulation failed'); } finally { setRunning(false); }
  }, [subAccountId, flowId, nodes, edges, sig, api, markSaved]);

  const decoratedNodes = useMemo(() => nodes.map((n) => {
    const data = { ...(n.data || {}), _onDelete: deleteNodeById, _unwiredHandles: getUnwiredHandleIds(n, edges) };
    if (n.type === 'start') data._phoneNumber = flow?.phone_number || '';
    if (pulsed[n.id]) data._pulse = pulsed[n.id];
    return { ...n, data, selected: n.id === selectedId, deletable: n.type !== 'start' };
  }), [nodes, edges, pulsed, deleteNodeById, flow?.phone_number, selectedId]);

  const decoratedEdges = useMemo(() => decorateEdges(edges, nodes, { hoveredNodeId, selectedNodeId: selectedId, editor: true }), [edges, nodes, hoveredNodeId, selectedId]);
  const selectedNode = nodes.find((n) => n.id === selectedId);

  const panel = selectedNode ? (
    <NodeConfigDrawer node={selectedNode} agents={agents} lineLimit={lineLimit} phoneNumber={flow?.phone_number} phoneNumbers={phoneNumbers}
      onPhoneNumberChange={updateFlowPhoneNumber} onPatch={patchNode} onDelete={() => deleteNodeById(selectedNode.id)} onClose={() => setSelectedId(null)} />
  ) : null;

  return (
    <div data-vox-theme={isDarkMode ? 'dark' : 'light'} className="h-full flex flex-col overflow-hidden" style={{ background: 'var(--vox-canvas-bg)', color: 'var(--vox-text-primary)' }} data-testid="callflow-canvas-page">
      <header className="flex-shrink-0 h-14 px-4 flex items-center gap-3 border-b" style={{ background: 'var(--vox-chrome-bg)', borderColor: 'var(--vox-chrome-border)' }}>
        <button onClick={() => navigate(`/workspace/${flow?.workspace_id || ''}/sub-account/${subAccountId}/call-routing`)} className="p-1.5 rounded hover:bg-black/5 dark:hover:bg-white/5" style={{ color: 'var(--vox-text-secondary)' }} data-testid="canvas-back">
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate" data-testid="canvas-flow-name">{flow?.name || 'Loading flow…'}</div>
          <div className="text-[10px]" style={{ color: 'var(--vox-text-muted)' }}>{flow?.phone_number}</div>
        </div>
        <button onClick={toggleEnabled} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border ${flow?.enabled ? 'bg-emerald-400/15 text-emerald-600 dark:text-emerald-300 border-emerald-400/30' : 'bg-black/5 dark:bg-white/5 text-slate-500 dark:text-gray-400 border-slate-200 dark:border-white/10'}`} data-testid="canvas-toggle-enabled">
          {flow?.enabled ? <ToggleRight size={13} /> : <ToggleLeft size={13} />} {flow?.enabled ? 'Live' : 'Disabled'}
        </button>
        <button onClick={tidy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/5 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-gray-300 text-[11px] hover:bg-black/10 dark:hover:bg-white/10" data-testid="canvas-tidy">
          <Sparkles size={13} /> Tidy
        </button>
        <button onClick={runSimulation} disabled={running} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-400/15 border border-emerald-400/40 text-emerald-600 dark:text-emerald-300 text-[11px] font-bold hover:bg-emerald-400/25 disabled:opacity-50" data-testid="canvas-run" title="Simulate the flow end-to-end">
          {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Test
        </button>
        <div className="w-px h-6 bg-slate-200 dark:bg-white/10" />
        <PublishControls saveState={saveState} hasDraft={hasDraft} publishing={publishing} onPublish={publish} />
      </header>

      <div className="flex-1 flex overflow-hidden relative">
        <div ref={flowRef} className="flex-1 relative" onDragOver={onDragOver} onDrop={onDrop} data-testid="callflow-canvas">
          <ReactFlow className="voxinity-flow" nodes={decoratedNodes} edges={decoratedEdges} nodeTypes={nodeTypes} edgeTypes={edgeTypes}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect} onConnectEnd={onConnectEnd}
            onReconnect={onReconnect} onReconnectStart={onReconnectStart} onReconnectEnd={onReconnectEnd}
            onNodeClick={(_, n) => setSelectedId(n.id)} onPaneClick={() => setSelectedId(null)}
            onNodeMouseEnter={(_, n) => setHoveredNodeId(n.id)} onNodeMouseLeave={() => setHoveredNodeId(null)}
            connectionRadius={48} reconnectRadius={16} snapToGrid snapGrid={[10, 10]} deleteKeyCode={['Backspace', 'Delete']}
            defaultViewport={flow?.canvas_viewport || { x: 80, y: 80, zoom: 0.85 }} fitView={!flow?.canvas_viewport} fitViewOptions={{ padding: 0.2 }}
            proOptions={{ hideAttribution: true }}>
            <Background color="var(--vox-grid)" gap={20} size={1} />
            <Controls position="bottom-left" />
            <MiniMap pannable zoomable nodeStrokeWidth={3} nodeColor={(n) => MINIMAP[n.type] || '#94a3b8'} />
          </ReactFlow>
          <AnimatePresence>{runResult && <RunResult result={runResult} onClose={() => setRunResult(null)} />}</AnimatePresence>
        </div>
        <FlowSettingsRail selectedPanel={panel} onTab={() => setSelectedId(null)} api={api} subAccountId={subAccountId} flow={flow} agents={agents}
          onFlowUpdated={setFlow} versionKey={versionKey}
          onRestored={(v) => { setNodes(v.nodes || []); setEdges(v.edges || []); setFlow((f) => ({ ...f, settings: v.settings || f?.settings })); }} />
      </div>
    </div>
  );
}

export default function CallFlowCanvasPage() {
  return (
    <SubAccountLayout>
      <ReactFlowProvider>
        <CanvasInner />
      </ReactFlowProvider>
    </SubAccountLayout>
  );
}
